import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { BaseApiService, AuthenticationService, EnvironmentConfig } from "../../core/core";
import {
  TelematicsDeviceV2,
  GpsAndFleetApiResponse,
  GpsAndFleetLocationData,
  SarekonDisableStarterResponse,
} from "../models/telematics-v2-device.model";

declare var environment: EnvironmentConfig;

/**
 * A device + its most recent provider location, or null if the provider had
 * no recent fix for the device.
 */
export interface TelematicsDeviceWithLocation {
  device: TelematicsDeviceV2;
  location: GpsAndFleetLocationData | null;
}

@Injectable({
  providedIn: "root",
})
export class TelematicsV2Service extends BaseApiService<TelematicsDeviceV2> {
  constructor(httpClient: HttpClient, authenticationService: AuthenticationService) {
    super(httpClient, environment.assetsApiDomain, "/api/assets/v2/inventory", true, authenticationService);
  }

  getDevices(inventoryId: string): Observable<TelematicsDeviceV2[]> {
    const headers = this.getAuth();
    return this.httpClient.get<TelematicsDeviceV2[]>(`${this.baseUrl}${this.endpoint}/${inventoryId}/telematics-v2`, { headers });
  }

  addDevice(inventoryId: string, device: TelematicsDeviceV2): Observable<TelematicsDeviceV2> {
    const headers = this.getAuth();
    return this.httpClient.post<TelematicsDeviceV2>(`${this.baseUrl}${this.endpoint}/${inventoryId}/telematics-v2`, device, { headers });
  }

  updateDevice(inventoryId: string, deviceId: string, device: TelematicsDeviceV2): Observable<TelematicsDeviceV2> {
    const headers = this.getAuth();
    return this.httpClient.put<TelematicsDeviceV2>(
      `${this.baseUrl}${this.endpoint}/${inventoryId}/telematics-v2/${deviceId}`,
      device,
      { headers },
    );
  }

  deleteDevice(inventoryId: string, deviceId: string): Observable<void> {
    const headers = this.getAuth();
    return this.httpClient.delete<void>(`${this.baseUrl}${this.endpoint}/${inventoryId}/telematics-v2/${deviceId}`, { headers });
  }

  getDeviceLocation(inventoryId: string, deviceUniqueId: string): Observable<GpsAndFleetApiResponse> {
    const headers = this.getAuth();
    return this.httpClient.get<GpsAndFleetApiResponse>(
      `${this.baseUrl}${this.endpoint}/${inventoryId}/telematics-v2/location/${deviceUniqueId}`,
      { headers },
    );
  }

  /**
   * Queues a SareKon Starter Auto-Disable command for the device. Only succeeds
   * when the device exists on SareKon (GpsAndFleet-only devices will fail).
   */
  disableStarter(inventoryId: string, deviceUniqueId: string): Observable<SarekonDisableStarterResponse> {
    const headers = this.getAuth();
    return this.httpClient.post<SarekonDisableStarterResponse>(
      `${this.baseUrl}${this.endpoint}/${inventoryId}/telematics-v2/disable-starter/${encodeURIComponent(deviceUniqueId)}`,
      {},
      { headers },
    );
  }

  /**
   * Account-scoped device list. Returns every active telematics device the
   * account owns in a single intra-partition Cosmos query — no fan-out per
   * machine.
   */
  getDevicesForAccount(accountId: string): Observable<TelematicsDeviceV2[]> {
    const headers = this.getAuth();
    return this.httpClient.get<TelematicsDeviceV2[]>(
      `${this.baseUrl}/api/assets/v2/accounts/${accountId}/telematics-v2`,
      { headers },
    );
  }

  /**
   * Admin-wide list of every active telematics device that is attached to an
   * inventory item. Used by Rubbl Admin Inventory → GPS Devices.
   */
  getAllAttachedDevices(): Observable<TelematicsDeviceV2[]> {
    const headers = this.getAuth();
    return this.httpClient.get<TelematicsDeviceV2[]>(
      `${this.baseUrl}/api/assets/v2/telematics-v2/devices`,
      { headers },
    );
  }

  /**
   * Create a device that isn't attached to a machine yet. Use addDevice when
   * the attachment is known up-front.
   */
  addUnattachedDevice(accountId: string, device: TelematicsDeviceV2): Observable<TelematicsDeviceV2> {
    const headers = this.getAuth();
    return this.httpClient.post<TelematicsDeviceV2>(
      `${this.baseUrl}/api/assets/v2/accounts/${accountId}/telematics-v2`,
      device,
      { headers },
    );
  }

  /**
   * Account-scoped update — works for both attached and unattached devices.
   */
  updateDeviceForAccount(accountId: string, deviceId: string, device: TelematicsDeviceV2): Observable<TelematicsDeviceV2> {
    const headers = this.getAuth();
    return this.httpClient.put<TelematicsDeviceV2>(
      `${this.baseUrl}/api/assets/v2/accounts/${accountId}/telematics-v2/${deviceId}`,
      device,
      { headers },
    );
  }

  /**
   * Account-scoped soft-delete — works for both attached and unattached devices.
   */
  deleteDeviceForAccount(accountId: string, deviceId: string): Observable<void> {
    const headers = this.getAuth();
    return this.httpClient.delete<void>(
      `${this.baseUrl}/api/assets/v2/accounts/${accountId}/telematics-v2/${deviceId}`,
      { headers },
    );
  }

  /**
   * Convenience wrapper around `getDeviceLocation` that hides the provider
   * response envelope. Returns the most recent fix or `null` if the provider
   * has none. Errors are swallowed and treated as "no fix" so consumers can
   * render a single device without its location request taking down the page.
   *
   * Reusable across admin & website consumers — the server normalizes
   * GPS-and-Fleet's two response shapes, but defending here keeps the UI
   * resilient to transient provider hiccups.
   */
  getLatestLocation(inventoryId: string, deviceUniqueId: string): Observable<GpsAndFleetLocationData | null> {
    return this.getDeviceLocation(inventoryId, deviceUniqueId).pipe(
      map(response => {
        if (!response || !response.status) return null;
        const data = response.data || [];
        return data.length > 0 ? data[0] : null;
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * Bulk variant — fetch the latest location for every supplied device in
   * parallel and return them keyed by IMEI / deviceUniqueId. Devices without
   * a fix are simply absent from the map (callers can branch on `has(imei)`).
   *
   * `inventoryIdFor` is a callback that resolves the parent machine id for
   * a given device, since the location endpoint is keyed under inventory.
   */
  getLatestLocations(
    devices: TelematicsDeviceV2[],
    inventoryIdFor: (device: TelematicsDeviceV2) => string | null | undefined,
  ): Observable<Map<string, GpsAndFleetLocationData>> {
    const requests = devices
      .filter(d => d && d.deviceUniqueId && d.status === "Active")
      .map(d => {
        const machineId = inventoryIdFor(d) || d.machineId;
        if (!machineId) return of(null);
        return this.getLatestLocation(machineId, d.deviceUniqueId).pipe(
          map(loc => (loc ? { imei: d.deviceUniqueId, location: loc } : null)),
        );
      });

    if (requests.length === 0) {
      return of(new Map<string, GpsAndFleetLocationData>());
    }

    return forkJoin(requests).pipe(
      map(results => {
        const out = new Map<string, GpsAndFleetLocationData>();
        for (const r of results) {
          if (r) out.set(r.imei, r.location);
        }
        return out;
      }),
    );
  }
}
